import { useEffect, useState } from "react";

import { DrawerProps, Drawer, Descriptions } from "antd";
import SVGIcon from "../../../../utils/SVGIcon";
import Config from "../../../../config/Config";
import CancelButton from "../../../../components/Buttons/CancelButton";
import { useAppSelector, useAppDispatch } from "../../../../utils/hooks/hooks";
import { renderNA, toUpperCaseWithUnderscore } from "../../../../utils/commonFunctions";
import { downloadBlobFile } from "../../../../config/Global";
import { genderOptions } from "../../../../config/Constant";
import dayjs from "dayjs";
import { dateFormats } from "../utils/constants";
import CreateButton from "../../../../components/ToolBar/Buttons/CreateButton";
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { Col, Row } from "antd";
import { Avatar, Button, List, Skeleton, Card, Space, message, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
const { Title, Link, Text } = Typography;
import AccessCodeListing from "./Listing/AccessCodeListing";
import { exportFile } from "../utils/slice";


interface ViewDrawerProps extends DrawerProps {
  titleName: string;
  open: boolean;
  close: () => void;
}

/**
 * Renders a view drawer component for displaying access codes details.
 *
 * @param {ViewDrawerProps} props - The component props.
 * @param {string} props.titleName - The title of the drawer.
 * @param {boolean} props.open - Whether the drawer is open or not.
 * @param {function} props.close - The function to close the drawer.
 * @param {...rest} props.rest - Additional props for the component.
 * @return {JSX.Element} The rendered view drawer component.
 */
const ViewDrawer: React.FC<ViewDrawerProps> = ({
  titleName,
  open,
  close,
  ...rest
}) => {
  const details = useAppSelector((state) => state.customerAccess.details);
  const [data, setData] = useState<any[]>([]);
  const [list, setList] = useState<any[]>([]);
  const [initLoading, setInitLoading] = useState(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (details) {
      setInitLoading(false);
      setData(details.access_codes);
      setList(details.access_codes);
    }
  }, [details])


  const exportToUser = () => {
    dispatch(exportFile(details.id))
      .then((data: any) => {
        downloadBlobFile(data, toUpperCaseWithUnderscore(details.campaign_name) + '.csv', 'text/csv;charset=utf-8;')
       
      })
      .catch((error: any) => error);
  };



  const drawerClose = () => {
    close();
  };

  return (
    <Drawer
      title={`View ${titleName}`}
      open={open}
      placement="right"
      width={"70%"}
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[<CancelButton key={2} onClick={drawerClose} />]}
      {...rest}
    >

      <Descriptions column={2} bordered layout="horizontal">
        <Descriptions.Item label="Subscription Name">
          {renderNA(details?.campaign_name)}
        </Descriptions.Item>
        <Descriptions.Item label="Total Access Codes">
          {renderNA(details?.access_code_count)}
        </Descriptions.Item>
        <Descriptions.Item label="Subscription Unique Code">
          {renderNA(details?.short_code)}
        </Descriptions.Item>
        <Descriptions.Item label="Start Date">
          {details?.start_date
            ? dayjs(details?.start_date).format(dateFormats.start_date)
            : "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="End Date">
          {details?.end_date
            ? dayjs(details?.end_date).format(dateFormats.end_date)
            : "N/A"}
        </Descriptions.Item>
        

        <Descriptions.Item label="Access Code Type">
          {details?.special_flag && details.special_flag == 1 ? 'Internal' : 'Regular'}
        </Descriptions.Item>
        {details?.industry.length > 0 &&
          <Descriptions.Item label="Industry">
            { details.industry[0].industry_name}
          </Descriptions.Item>
        }
        <Descriptions.Item label="Description">
          {renderNA(details?.description)}
        </Descriptions.Item>

      </Descriptions>


      {details?.categories &&
        <Space direction="vertical" size="middle" style={{ margin: ' 3rem 0', width: '100%' }}>
          <Title level={5}>Career Categories</Title>
          <List
            size="small"
            bordered
            dataSource={details.categories}
            renderItem={(item: any) =>
              <List.Item>
                <List.Item.Meta
                  title={<><Text type="secondary"> {item.code}</Text><Text > - {item.name}</Text></>}
                />
              </List.Item>
            }
          />
        </Space>
      }

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <AccessCodeListing />
      </Space>

    </Drawer>
  );
};

export default ViewDrawer;
